import { Component, ErrorInfo, ReactNode, PropsWithChildren } from 'react';
import { logger } from '@/core/monitoring';
import './ErrorBoundary.css';

interface Props extends PropsWithChildren {
    fallback?: ReactNode;
    onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
    hasError: boolean;
    error?: Error;
}

/**
 * Error Boundary Component
 * Catches JavaScript errors in child component tree
 */
export class ErrorBoundary extends Component<Props, State> {
    public state: State = {
        hasError: false,
    };

    public static getDerivedStateFromError(error: Error): State {
        return { hasError: true, error };
    }

    public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
        logger.error('ErrorBoundary caught an error', {
            error: error.message,
            stack: error.stack,
            componentStack: errorInfo.componentStack,
        });

        this.props.onError?.(error, errorInfo);
    }

    private handleReset = () => {
        this.setState({ hasError: false, error: undefined });
    };

    public render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                return this.props.fallback;
            }

            return (
                <div className="error-boundary">
                    <div className="error-boundary__container">
                        <h1 className="error-boundary__title">Something went wrong</h1>
                        <p className="error-boundary__message">
                            Sorry, something went wrong on our end. We&apos;ve been notified and are looking into it.
                        </p>
                        {this.state.error && (
                            <details className="error-boundary__details">
                                <summary>Error details</summary>
                                <pre className="error-boundary__stack">{this.state.error.message}</pre>
                            </details>
                        )}
                        <button className="error-boundary__button" onClick={this.handleReset}>
                            Try again
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}
